import React, { useMemo, useState } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ImageOff,
  Lock,
  RefreshCw,
  Search,
  Users,
  XCircle,
} from 'lucide-react';

import { useBlobImageUrl } from '../../hooks/useBlobImageUrl';
import type {
  EmployeeEnrollment,
  EnrollmentFailureReason,
  EnrollmentIssue,
  EnrollmentMatchedEmployee,
} from '../../interfaces/SelfieEnrollment';
import { EnmSpin, EnrollmentChip } from '../../components/enrollment/EnrollmentChip';
import {
  ENROLL_REASONS,
  ENROLL_REASON_ORDER,
  enrollmentReason,
  enrollmentReasonKey,
  fmtAgo,
  fmtWhen,
  pctLabel,
} from '../../util/enrollmentReasons';
import { Avatar } from './SelfieExceptionsPage';

/**
 * Selfie Exceptions → Enrollment view (TAS-568). Current state, not tied to a
 * week: everyone active whose enrollment photo Azure rejected or is still
 * checking (GET /admin/selfieEnrollment/issues). Ported from the design
 * bundle's enroll/enroll-tab.js.
 *
 * The page owns the issues list (the tab row counts it, the shared photo modal
 * patches it); this view owns only its filters and the see-both toggles.
 */

type EstFilter = 'all' | 'failed' | 'verifying';

export interface EnrollmentCounts {
  failed: number;
  verifying: number;
  byReason: Partial<Record<EnrollmentFailureReason, number>>;
}

/** Counted client-side so an in-place Retry or Fix moves the tiles immediately. */
export function countEnrollmentIssues(issues: EnrollmentIssue[] | null): EnrollmentCounts {
  const c: EnrollmentCounts = { failed: 0, verifying: 0, byReason: {} };
  (issues ?? []).forEach((i) => {
    const n = i.enrollment;
    if (n.state === 'failed') {
      c.failed++;
      const k = enrollmentReasonKey(n.reason);
      c.byReason[k] = (c.byReason[k] ?? 0) + 1;
    } else if (n.state === 'verifying') {
      c.verifying++;
    }
  });
  return c;
}

const isOpenIssue = (i: EnrollmentIssue) => i.enrollment.state === 'failed' || i.enrollment.state === 'verifying';

interface Props {
  issues: EnrollmentIssue[] | null;
  loading: boolean;
  error: string | null;
  canEdit: boolean;
  /** employeeID → sort time. A row retried in place keeps its slot until the next Refresh. */
  pins: Record<number, string>;
  /** employeeID of the row to flash once. */
  flashId: number | null;
  /** employeeIDs whose inline Retry is in flight. */
  retrying: number[];
  onReload: () => void;
  onRetry: (issue: EnrollmentIssue) => void;
  onFix: (issue: EnrollmentIssue) => void;
  onOpenOther: (matched: EnrollmentMatchedEmployee) => void;
}

/* ── thumbnails: one hook per image, never a shared blob URL ─────────────── */

function EnrollThumb({ path, failed, alt }: { path: string | null; failed: boolean; alt: string }) {
  const { blobUrl, loading, error } = useBlobImageUrl(path);
  const state = !path || error ? ' failed' : loading || !blobUrl ? ' loading' : '';
  return (
    <span className={`thumb en-thumb ${failed ? 'fail' : 'ver'}${state}`}>
      {blobUrl && !loading && !error && <img src={blobUrl} alt={alt} />}
      {(!path || !!error) && <ImageOff />}
    </span>
  );
}

function ComparePic({ path, alt }: { path: string | null; alt: string }) {
  const { blobUrl, loading, error } = useBlobImageUrl(path);
  return (
    <div className={`pic${loading ? ' loading' : ''}`}>
      {blobUrl && !loading && !error ? <img src={blobUrl} alt={alt} /> : !loading ? <ImageOff /> : null}
    </div>
  );
}

/* ── rows ─────────────────────────────────────────────────────────────── */

function EnrollmentRow({
  issue,
  canEdit,
  open,
  flash,
  retrying,
  onToggle,
  onRetry,
  onFix,
  onOpenOther,
}: {
  issue: EnrollmentIssue;
  canEdit: boolean;
  open: boolean;
  flash: boolean;
  retrying: boolean;
  onToggle: () => void;
  onRetry: () => void;
  onFix: () => void;
  onOpenOther: (m: EnrollmentMatchedEmployee) => void;
}) {
  const e = issue.employee;
  const n: EmployeeEnrollment = issue.enrollment;
  const failed = n.state === 'failed';
  const meta = enrollmentReason(n.reason);
  const matched = failed && enrollmentReasonKey(n.reason) === 'duplicate' ? n.matchedEmployee ?? null : null;
  const attempts = n.attempts ?? 1;
  const name = `${e.firstName} ${e.lastName}`;
  const where = [e.locationName, e.supervisorName].filter(Boolean).join(' · ');
  const retryPrimary = failed && meta.emphasis === 'retry';

  return (
    <>
      <tr className={`enrow${flash ? ' flash' : ''}`}>
        <td className="col-thumb">
          <EnrollThumb path={e.enrollmentPhotoUrl} failed={failed} alt={`Enrollment photo for ${name}`} />
        </td>
        <td>
          <div className="emp-cell">
            <Avatar employeeID={e.employeeID} firstName={e.firstName} lastName={e.lastName} />
            <div>
              <div className="en">{name}</div>
              <div className="eid">{e.altEmployeeID}</div>
              {where && <div className="en-where-inline">{where}</div>}
            </div>
          </div>
        </td>
        <td className="col-where">
          <div className="en-where">
            {e.locationName ?? '—'}
            {e.supervisorName && <div className="s">Sup. {e.supervisorName}</div>}
          </div>
        </td>
        <td>
          <EnrollmentChip enrollment={n} />
          <span className="enm-fam">{failed ? meta.family : 'checking with Azure'}</span>
          {matched && (
            <button type="button" className={`en-match${open ? ' open' : ''}`} onClick={onToggle}>
              matches{' '}
              <b>
                {matched.firstName} {matched.lastName}
              </b>
              {n.confidence != null && <span className="c">{pctLabel(n.confidence)}</span>}
              <ChevronDown />
            </button>
          )}
        </td>
        <td>
          {failed ? (
            <div className="en-when">
              Failed {fmtWhen(n.failedAt)}
              <div className="s">
                {fmtAgo(n.failedAt)}
                {attempts > 1 ? ` · after ${attempts} tries` : ''}
              </div>
            </div>
          ) : (
            <div className="en-when">
              Uploaded {fmtWhen(n.queuedAt ?? n.enrolledAt)}
              <div className="s">
                {fmtAgo(n.queuedAt ?? n.enrolledAt)}
                {attempts > 1 ? ` · try ${attempts} of 3` : ''}
              </div>
            </div>
          )}
        </td>
        <td className="col-by">
          {n.requestedBy ? (
            <span className="en-by">{n.requestedBy.name}</span>
          ) : (
            <span className="en-by none" title="Queued before uploads were attributed">
              Not recorded
            </span>
          )}
        </td>
        <td>
          <div className="en-acts">
            {!canEdit ? (
              <button type="button" className="btn sm" onClick={onFix}>
                View
              </button>
            ) : failed ? (
              <>
                <button
                  type="button"
                  className={`btn sm${retryPrimary ? ' fix' : ''}`}
                  onClick={onRetry}
                  disabled={retrying}
                >
                  {retrying ? <EnmSpin light={retryPrimary} /> : <RefreshCw />}
                  {retrying ? 'Queuing' : 'Retry'}
                </button>
                <button type="button" className={`btn sm${retryPrimary ? '' : ' fix'}`} onClick={onFix}>
                  Fix
                </button>
              </>
            ) : (
              <>
                <button type="button" className="btn sm" disabled title="Already checking">
                  <RefreshCw />
                  Retry
                </button>
                <button type="button" className="btn sm" onClick={onFix}>
                  View
                </button>
              </>
            )}
          </div>
        </td>
      </tr>
      {matched && open && (
        <tr className="en-cmp">
          <td colSpan={7}>
            <div className="en-cmp-in">
              <div className="en-cmp-card">
                <div className="lab">This upload</div>
                <ComparePic path={e.enrollmentPhotoUrl} alt={`Rejected upload for ${name}`} />
                <div className="who">
                  {name}
                  <span className="enm-mono">{e.altEmployeeID}</span>
                </div>
              </div>
              <div className="en-cmp-card">
                <div className="lab">Already enrolled as</div>
                <ComparePic
                  path={matched.enrollmentPhotoUrl}
                  alt={`Enrollment photo for ${matched.firstName} ${matched.lastName}`}
                />
                <div className="who">
                  {matched.firstName} {matched.lastName}
                  <span className="enm-mono">{matched.altEmployeeID}</span>
                  {!matched.active && <span className="enm-act off">Inactive</span>}
                </div>
              </div>
              <div className="en-cmp-say">
                {n.confidence != null && <span className="big">{pctLabel(n.confidence)}</span>}
                match, over the 80% duplicate threshold. {ENROLL_REASONS.duplicate.doit(e)}
                {canEdit && (
                  <div className="acts">
                    <button type="button" className="btn sm fix" onClick={onFix}>
                      Fix {e.firstName.trim()}’s photo
                    </button>
                    <button type="button" className="btn sm" onClick={() => onOpenOther(matched)}>
                      Open {matched.firstName} {matched.lastName} ({matched.altEmployeeID})
                    </button>
                  </div>
                )}
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

/* ── view ─────────────────────────────────────────────────────────────── */

const SelfieEnrollmentView: React.FC<Props> = ({
  issues,
  loading,
  error,
  canEdit,
  pins,
  flashId,
  retrying,
  onReload,
  onRetry,
  onFix,
  onOpenOther,
}) => {
  const [est, setEst] = useState<EstFilter>('all');
  const [reasons, setReasons] = useState<EnrollmentFailureReason[]>([]);
  const [query, setQuery] = useState('');
  const [openCmp, setOpenCmp] = useState<Record<number, boolean>>({});

  const counts = useMemo(() => countEnrollmentIssues(issues), [issues]);

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    const sortTime = (i: EnrollmentIssue) =>
      new Date(
        pins[i.employee.employeeID] ?? i.enrollment.failedAt ?? i.enrollment.queuedAt ?? i.enrollment.enrolledAt ?? 0
      ).getTime();
    return (issues ?? [])
      .filter(isOpenIssue)
      .filter((i) => {
        const n = i.enrollment;
        const e = i.employee;
        if (est !== 'all' && n.state !== est) return false;
        if (reasons.length && (n.state !== 'failed' || !reasons.includes(enrollmentReasonKey(n.reason)))) return false;
        if (q && !`${e.firstName} ${e.lastName} ${e.altEmployeeID}`.toLowerCase().includes(q)) return false;
        return true;
      })
      .sort((a, b) => {
        // Failed first (a row retried in place counts as failed until the next Refresh), newest first.
        const fa = a.enrollment.state === 'failed' || a.employee.employeeID in pins;
        const fb = b.enrollment.state === 'failed' || b.employee.employeeID in pins;
        if (fa !== fb) return fa ? -1 : 1;
        return sortTime(b) - sortTime(a);
      });
  }, [issues, est, reasons, query, pins]);

  const filtersActive = est !== 'all' || reasons.length > 0 || query.trim() !== '';
  const clear = () => {
    setEst('all');
    setReasons([]);
    setQuery('');
  };

  if (error && !issues) {
    return (
      <div className="errbanner">
        <AlertTriangle />
        <div>
          <b>Couldn’t load enrollment status.</b>
          <span className="em">{error} · GET /admin/selfieEnrollment/issues failed</span>
        </div>
        <button type="button" className="btn sm" onClick={onReload} disabled={loading}>
          <RefreshCw className={loading ? 'spin' : ''} />
          Try again
        </button>
      </div>
    );
  }

  if (!issues) {
    return (
      <div className="skel-card">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="skel-row">
            <span className="sk thumb-sk" />
            <span className="sk av-sk" />
            <span className="sk w1" />
            <span className="sk w2" />
            <span className="sk w3" />
            <span className="sk w4" />
          </div>
        ))}
      </div>
    );
  }

  const errBanner = error ? (
    <div className="errbanner">
      <AlertTriangle />
      <div>
        <b>Couldn’t refresh enrollment status.</b>
        <span className="em">{error} · GET /admin/selfieEnrollment/issues failed</span>
      </div>
      <button type="button" className="btn sm" onClick={onReload} disabled={loading}>
        <RefreshCw className={loading ? 'spin' : ''} />
        Try again
      </button>
    </div>
  ) : null;

  if (!counts.failed && !counts.verifying) {
    return (
      <>
        {errBanner}
        <div className="state good enr-good">
          <div className="st-ic">
            <CheckCircle2 />
          </div>
          <h3>Every enrollment photo is verified</h3>
          <p>
            No failed or in-progress enrollments. New uploads show up here while Azure checks them, and stay here if
            Azure rejects them.
          </p>
        </div>
      </>
    );
  }

  const mixKeys = ENROLL_REASON_ORDER.filter((k) => counts.byReason[k]);
  const segs: [EstFilter, string, number][] = [
    ['all', 'All', counts.failed + counts.verifying],
    ['failed', 'Failed', counts.failed],
    ['verifying', 'Verifying', counts.verifying],
  ];

  return (
    <>
      {errBanner}

      <div className="en-kpis">
        <div className="en-kpi fail">
          <div className="kl">
            <AlertTriangle />
            Failed
          </div>
          <div className={`kv${counts.failed ? '' : ' zero'}`}>{counts.failed}</div>
          <div className="ks">can’t face-match until fixed</div>
        </div>
        <div className="en-kpi ver">
          <div className="kl">
            <EnmSpin />
            Verifying
          </div>
          <div className={`kv${counts.verifying ? '' : ' zero'}`}>{counts.verifying}</div>
          <div className="ks">Azure is still checking, usually within 15 min</div>
        </div>
        <div className="en-kpi mix">
          <div className="kl">
            <Users />
            Why they failed
          </div>
          {counts.failed ? (
            <>
              <div className="en-mix">
                {mixKeys.map((k) => (
                  <span
                    key={k}
                    style={{
                      width: `${((counts.byReason[k] ?? 0) / counts.failed) * 100}%`,
                      background: ENROLL_REASONS[k].dot,
                    }}
                  />
                ))}
              </div>
              <div className="en-leg">
                {mixKeys.map((k) => {
                  const m = ENROLL_REASONS[k];
                  const on = reasons.includes(k);
                  return (
                    <button
                      key={k}
                      type="button"
                      className={on ? 'on' : ''}
                      title={on ? 'Show every reason' : `Show only ${m.label}`}
                      onClick={() => setReasons(on ? [] : [k])}
                    >
                      <span className="dot" style={{ background: m.dot }} />
                      <b>{counts.byReason[k]}</b>
                      {m.label}
                      <span className="fam">{m.family}</span>
                    </button>
                  );
                })}
              </div>
            </>
          ) : (
            <div className="ks">Nothing failed.</div>
          )}
        </div>
      </div>

      <div className="en-bar">
        <div className="searchwrap">
          <Search />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search employee name or ID…"
          />
        </div>
        <div className="segmented" role="group" aria-label="Enrollment state">
          {segs.map(([k, label, count]) => (
            <button key={k} type="button" data-est={k} className={est === k ? 'on' : ''} onClick={() => setEst(k)}>
              {label}
              <span className="segcount">{count}</span>
            </button>
          ))}
        </div>
        {filtersActive && (
          <button type="button" className="clear-btn" onClick={clear}>
            <XCircle />
            Clear
          </button>
        )}
      </div>

      <div className="resultline">
        <span>
          <b>{rows.length}</b> {rows.length === 1 ? 'employee' : 'employees'} · failed first, newest first
        </span>
        {!canEdit && (
          <span className="en-ro-note">
            <Lock />
            view only: Retry and Fix need the Edit enrollment photos permission
          </span>
        )}
      </div>

      {rows.length === 0 ? (
        <div className="state zero">
          <div className="st-ic">
            <Search />
          </div>
          <h3>No one matches these filters</h3>
          <p>Clear a filter to see everyone with a failed or in-progress enrollment.</p>
          <div className="st-act">
            <button type="button" className="btn" onClick={clear}>
              Clear filters
            </button>
          </div>
        </div>
      ) : (
        <div className={`table-card${loading ? ' refreshing' : ''}`}>
          <table className="xt en-t">
            <thead>
              <tr>
                <th className="col-thumb">Photo</th>
                <th>Employee</th>
                <th className="col-where">Location</th>
                <th>Status</th>
                <th>When</th>
                <th className="col-by">Uploaded by</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((i) => {
                const id = i.employee.employeeID;
                return (
                  <EnrollmentRow
                    key={id}
                    issue={i}
                    canEdit={canEdit}
                    open={!!openCmp[id]}
                    flash={flashId === id}
                    retrying={retrying.includes(id)}
                    onToggle={() => setOpenCmp((p) => ({ ...p, [id]: !p[id] }))}
                    onRetry={() => onRetry(i)}
                    onFix={() => onFix(i)}
                    onOpenOther={onOpenOther}
                  />
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
};

export default SelfieEnrollmentView;
